import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  FileText,
  User,
  LogOut,
  Menu,
  X,
  Trophy
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: getAuthHeaders(),
          
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };
    fetchUserProfile();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
        
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear both storages and redirect
    localStorage.removeItem('session_token');
    sessionStorage.removeItem('session_token');
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Users, label: 'Referrals', path: '/referrals' },
    { icon: DollarSign, label: 'Payouts', path: '/payouts' },
    { icon: FileText, label: 'Resources', path: '/resources' },
    { icon: Trophy, label: 'Tiers', path: '/tiers' },
    { icon: User, label: 'Profile', path: '/profile' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_agent-earnings-5/artifacts/95ok324x_KB%20-%20Final%20Logo%20%28Updated%29-01.png"
              alt="KILO BEATERS"
              className="h-10"
            />
            <Button
              data-testid="close-sidebar-button"
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}-button`}
                  variant={isActive ? 'secondary' : 'ghost'}
                  className={`w-full justify-start ${
                    isActive ? 'bg-primary/10 text-primary hover:bg-primary/20' : ''
                  }`}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.label}
                </Button>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border">
          <Button
            data-testid="logout-button"
            variant="ghost"
            className="w-full justify-start text-destructive hover:text-destructive"
            onClick={handleLogout}
          >
            <LogOut className="h-5 w-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="open-sidebar-button"
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Logo - visible on mobile, hidden on desktop (already in sidebar) */}
            <div className="flex-1 flex justify-center lg:hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_agent-earnings-5/artifacts/95ok324x_KB%20-%20Final%20Logo%20%28Updated%29-01.png"
                alt="KILO BEATERS"
                className="h-8"
              />
            </div>
            <div className="hidden lg:flex lg:flex-1" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-button" variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {user?.name?.charAt(0)?.toUpperCase() || 'P'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name || 'Partner'}</span>
                    <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-10 lg:p-12">
          {children}
        </main>
      </div>
    </div>
  );
}
